import { Component, OnInit, signal } from '@angular/core';
import { UserService } from '../../services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  Validators,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { PasswordMatchValidator } from '../../validators/passwords-match.validator';
import { MeService } from '../../services/me.service';
import { ChangePassword } from '../../models/change-password.model';
import { UserProfile } from '../../models/user-profile.model';
import { User } from '../../models/user.model';
import { EmptyFormValidator } from '../../validators/create-account-form.validator';
import { UserProfileService } from '../../services/user-profile.service';
import { states } from '../../constants';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'app-admins',
  standalone: true,
  imports: [
    MatCardModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    MatSelectModule,
    NgxMaskDirective,
    MatProgressBarModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './account-info.component.html',
  styleUrl: './account-info.component.scss',
})
export class AccountInfoComponent implements OnInit {
  loading: boolean = false;
  states = states;
  userProfile?: UserProfile;
  hideCurrentPassword = signal(true);

  clickCurrPass(event: MouseEvent) {
    this.hideCurrentPassword.set(!this.hideCurrentPassword());
    event.stopPropagation();
  }

  hideNewPassword = signal(true);
  clickNewPass(event: MouseEvent) {
    this.hideNewPassword.set(!this.hideNewPassword());
    event.stopPropagation();
  }

  hideConfirmNewPassword = signal(true);
  clickConfirmNewPass(event: MouseEvent) {
    this.hideConfirmNewPassword.set(!this.hideConfirmNewPassword());
    event.stopPropagation();
  }

  vendorId: string = '';

  userForm = new FormGroup({
    id: new FormControl(''),
    firstName: new FormControl('', [Validators.required, EmptyFormValidator]),
    lastName: new FormControl('', [Validators.required, EmptyFormValidator]),
    email: new FormControl('', [Validators.required, EmptyFormValidator]),
    phone: new FormControl('', Validators.required),
  });

  userProfileForm = new FormGroup({
    id: new FormControl(''),
    street: new FormControl('', [Validators.required, EmptyFormValidator]),
    address2: new FormControl(''),
    city: new FormControl('', [Validators.required, EmptyFormValidator]),
    state: new FormControl('', [Validators.required, EmptyFormValidator]),
    zipCode: new FormControl('', [Validators.required, EmptyFormValidator]),
  });

  editPasswordForm = new FormGroup(
    {
      currentPassword: new FormControl('', [
        Validators.required,
        EmptyFormValidator,
      ]),
      password: new FormControl('', [Validators.required, EmptyFormValidator]),
      confirmPassword: new FormControl('', [
        Validators.required,
        EmptyFormValidator,
      ]),
    },
    { validators: PasswordMatchValidator }
  );

  constructor(
    private userService: UserService,
    private snackBar: MatSnackBar,
    private meService: MeService,
    private userProfileService: UserProfileService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.meService.getMyUserProfile().subscribe({
      next: (response) => {
        this.userProfile = response;
        this.userForm.controls.id.setValue(response.userId);
        this.userForm.controls.firstName.setValue(response.user.firstName);
        this.userForm.controls.lastName.setValue(response.user.lastName);
        this.userForm.controls.email.setValue(response.user.email);
        this.userForm.controls.phone.setValue(response.phone);

        this.userProfileForm.controls.id.setValue(response.id);
        this.userProfileForm.controls.street.setValue(response.street);
        this.userProfileForm.controls.address2.setValue(response.address2);
        this.userProfileForm.controls.city.setValue(response.city);
        this.userProfileForm.controls.state.setValue(response.state);
        this.userProfileForm.controls.zipCode.setValue(response.zipCode);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.openSnackbar();
      },
    });
  }

  onSave(form?: string) {
    var requests: Observable<any>[] = [];
    this.loading = true;
    const updateUserInfo: User = this.userForm.value as User;
    const updateUserProfileInfo: UserProfile = this.userProfileForm
      .value as UserProfile;

    if (
      updateUserProfileInfo.id != null &&
      updateUserProfileInfo.id.trim() != '' &&
      (!form || form?.toUpperCase() == 'USERPROFILEFORM')
    ) {
      requests.push(this.userProfileService.update(updateUserProfileInfo));
    }

    if (
      updateUserInfo.id != null &&
      updateUserInfo.id.trim() != '' &&
      (!form || form?.toUpperCase() == 'USERFORM')
    ) {
      requests.push(this.userService.update(updateUserInfo));
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.loading = false;
        this.openSnackbar('Account information saved successfully!');
      },
      error: () => {
        this.loading = false;
        this.openSnackbar();
      },
    });
  }

  onChangePassword() {
    this.loading = true;

    const changeUserPassword: ChangePassword = {
      newPassword: this.editPasswordForm.controls.password.value!,
      currentPassword: this.editPasswordForm.controls.currentPassword.value!,
    };

    this.meService.changePassword(changeUserPassword).subscribe({
      next: () => {
        this.loading = false;
        this.openSnackbar('Password changed successfully.');
        this.editPasswordForm.reset();
      },
      error: () => {
        this.loading = false;
        this.openSnackbar();
      },
    });
  }

  clearForm(form?: string) {
    if (form?.toUpperCase() == 'USERFORM' || !form) {
      this.userForm.reset();
      this.userForm.controls.id.setValue(this.userProfile?.userId ?? '');
    }

    if (form?.toUpperCase() === 'USERPROFILEFORM' || !form) {
      this.userProfileForm.reset();
      this.userProfileForm.controls.id.setValue(this.userProfile?.id ?? '');
    }
  }

  openSnackbar(message?: string) {
    this.snackBar.open(
      message ?? 'An error has occurred. Please try again.',
      '',
      { duration: 5000 }
    );
  }

  resetForm(form?: string) {
    if (form?.toUpperCase() === 'USERPROFILEFORM' || !form) {
      this.userProfileForm.controls.id.setValue(this.userProfile?.id ?? '');
      this.userProfileForm.controls.street.setValue(
        this.userProfile?.street ?? ''
      );
      this.userProfileForm.controls.address2.setValue(
        this.userProfile?.address2 ?? ''
      );
      this.userProfileForm.controls.city.setValue(this.userProfile?.city ?? '');
      this.userProfileForm.controls.state.setValue(
        this.userProfile?.state ?? ''
      );
      this.userProfileForm.controls.zipCode.setValue(
        this.userProfile?.zipCode ?? ''
      );
    }

    if (form?.toUpperCase() === 'USERFORM' || !form) {
      this.userForm.controls.id.setValue(this.userProfile?.userId ?? '');
      this.userForm.controls.firstName.setValue(
        this.userProfile?.user.firstName ?? ''
      );
      this.userForm.controls.lastName.setValue(
        this.userProfile?.user.lastName ?? ''
      );
      this.userForm.controls.email.setValue(this.userProfile?.user.email ?? '');
      this.userForm.controls.phone.setValue(this.userProfile?.phone ?? '');
    }
  }
}
